// cSpell:ignore afgerekend Afrekening forfait Gebouw Scenariolaan
// The building (group) settlement takes everything (design 2026-10-16, A):
// the building's common costs, each split by its key, every unit's own
// (private) costs, and both provision slices of every lease. Each tenant
// gets one statement and one payment request per period. A unit settlement
// stays for a unit on its own and for an interim settlement of one unit
// inside a building, which takes only the unit's own costs and its private
// slice. A departure settles its lease's share of both, as before.
//
// Pure helpers behind the wizard (which costs, which slices), its screens
// (one card per "Gebouw" / "bus N") and the statements, so every screen and
// every document groups a building settlement the same way.

import {
  getLocalizedText,
  toLocale,
} from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { costSettlementStates } from "./costSettlementState.utils";
import { IAddressParts, unitAddressLabel } from "./groupUnits.utils";
import { roundCents, toCents } from "./money.utils";
import { ProvisionSlice } from "./provisionHistory";
import { settlementDisplayName } from "./settlementName.utils";
import {
  departureSettlementCosts,
  normalizeGroupSettlementCosts,
  PRIVATE_KEY_SUFFIX,
} from "./settlementView.utils";
import {
  ContractRef,
  Invoice,
  leaseProvisionOf,
  PerContractResult,
  Settlement,
  SettlementScope,
} from "./types";

const ALL_SLICES: ProvisionSlice[] = ["common", "private"];

// The slices of the provision a new settlement weighs: a building both, a
// unit inside a building (an interim settlement) its private slice only,
// and a unit on its own or a departure both.
export const settlementSlicesFor = (params: {
  scope: SettlementScope;
  unitInGroup: boolean;
  isDeparture: boolean;
}): ProvisionSlice[] => {
  const { scope, unitInGroup, isDeparture } = params;
  if (scope === "group") return ALL_SLICES;
  return unitInGroup && !isDeparture ? ["private"] : ALL_SLICES;
};

// The costs a new settlement reads, from what the wizard fetched: for a
// building all of them (its own and its units'), read as a group settlement
// reads them (a cost booked on the group is a common cost); for a departure
// its unit's and its group's (departureSettlementCosts); for a unit its own.
export const wizardSettlementCosts = (params: {
  fetched: Invoice[];
  scope?: SettlementScope;
  groupId?: string;
  departureGroupId?: string;
}): Invoice[] => {
  const { fetched, scope, groupId, departureGroupId } = params;
  if (departureGroupId) {
    return departureSettlementCosts(fetched, departureGroupId);
  }
  if (scope !== "group") return fetched;
  return normalizeGroupSettlementCosts(fetched, { scope, groupId });
};

// A lease as a settlement weighs its provision: the monthly provision of the
// slices it weighs (all of it when both). Its forfait kinds stay on the lease
// (forfaitCommon / forfaitPrivate); the engine leaves them out.
export const withSettlementSlices = (
  ref: ContractRef,
  slices: ProvisionSlice[],
): ContractRef => {
  const weighs = (slice: ProvisionSlice) => slices.includes(slice);
  const monthlyOfSlices = (): number | undefined => {
    if (weighs("common") && weighs("private")) return ref.monthlyProvision;
    if (weighs("common")) return ref.monthlyProvisionCommon ?? 0;
    return ref.monthlyProvisionPrivate ?? 0;
  };
  const monthlyProvision = monthlyOfSlices();
  return { ...ref, monthlyProvision, provisionSlices: slices };
};

// A confirmed or new settlement of a whole building that took its units'
// costs and both slices (a building settlement as decided on 2026-10-16).
// An older group settlement, which weighed the common slice only, is not:
// its screens and documents keep reading as they did.
export const isBuildingSettlement = (
  settlement: Pick<Settlement, "scope" | "snapshot">,
): boolean =>
  settlement.scope === "group" &&
  (settlement.snapshot?.provisionSlices ?? []).includes("private");

// ── Labels ──────────────────────────────────────────────────────────────────

// "Gebouw": the building's own costs and part, next to "bus N" for a unit's.
export const buildingLabel = (locale?: string): string =>
  getLocalizedText("costs.label.building", {}, 0, toLocale(locale));

// A building's address as its card names it ("Scenariolaan 1, 9000 Gent"),
// from the property record the wizard holds; undefined when not known.
export const buildingAddressOf = (building?: {
  code: string;
  address?: string;
  box?: string;
  addressParts?: IAddressParts;
}): string | undefined => {
  if (!building) return undefined;
  if (building.addressParts) return unitAddressLabel(building);
  return building.address?.trim() || undefined;
};

// The building's card in a settlement's cost choice and its "Al afgerekend
// in … (…)": "Gebouw: Scenariolaan 1, 9000 Gent", or "Gebouw" when its
// address is not known.
export const buildingSectionTitle = (address?: string): string =>
  address?.trim()
    ? getLocalizedText("settlement.invoices.section_building", {
        address: address.trim(),
      })
    : buildingLabel();

// Where each cost that a confirmed settlement other than `ownId` settled was
// settled, as a settlement's cost choice says it: "Al afgerekend in
// Afrekening 01/01–30/06/2026 (Scenariolaan 1 bus 2, 9000 Gent)", a
// building's own settlement as "(Gebouw: Scenariolaan 1, 9000 Gent)". A
// departure, which leaves its costs open, names none.
export const settledInLabels = (params: {
  settlements: Settlement[];
  ownId?: string;
  // A unit's label ("Scenariolaan 1 bus 2, 9000 Gent"); undefined when the
  // wizard does not know it.
  unitLabelOf: (unitId: string) => string | undefined;
  // A building's label ("Gebouw: Scenariolaan 1, 9000 Gent"); "Gebouw"
  // without it.
  buildingLabelOf?: (groupId: string) => string | undefined;
}): Record<string, string> => {
  const { settlements, ownId, unitLabelOf, buildingLabelOf } = params;
  const labels: Record<string, string> = {};
  const states = costSettlementStates(
    settlements.filter(settlement => settlement.id !== ownId),
  );
  for (const [costId, state] of states) {
    const holder = state.settledIn;
    if (!holder) continue;
    labels[costId] = getLocalizedText(
      "settlement.invoices.already_settled_in",
      {
        title: settlementDisplayName(holder),
        label:
          holder.scope === "group"
            ? buildingLabelOf?.(String(holder.groupId)) ?? buildingLabel()
            : unitLabelOf(String(holder.unitId)) ??
              holder.snapshot?.propertyLabel ??
              String(holder.unitId),
      },
    );
  }
  return labels;
};

// Where a cost of a building settlement belongs: the building's common
// costs, or the own costs of the unit it is booked on.
export type TCostSection = "building" | "unit";

export const costSectionOf = (invoice: Pick<Invoice, "scope">): TCostSection =>
  invoice.scope === "private" ? "unit" : "building";

// The breakdown key of a unit's own cost carries the private marker.
export const isUnitBreakdownKey = (key: string): boolean =>
  key.endsWith(PRIVATE_KEY_SUFFIX);

// Labels compared the way a person counts: "bus 2" before "bus 10".
export const compareUnitLabels = (a: string, b: string): number =>
  a.localeCompare(b, "nl", { numeric: true, sensitivity: "base" });

export interface ICostSection<TInvoice> {
  key: string;
  section: TCostSection;
  // The unit of a unit section.
  unitId?: string;
  label: string;
  invoices: TInvoice[];
}

// The costs of a building settlement in one card per label: the building's
// first ("Gebouw"), then each unit's own costs ("bus 1", "bus 2", …) in
// numeric order. Empty sections are left out.
export const costSections = <
  TInvoice extends Pick<Invoice, "scope" | "unitId">,
>(params: {
  invoices: TInvoice[];
  unitLabelOf: (unitId: string) => string;
  // What the units are ordered by ("bus 2" before "bus 10"); their label
  // when not given.
  unitSortLabelOf?: (unitId: string) => string;
  buildingTitle?: string;
}): ICostSection<TInvoice>[] => {
  const { invoices, unitLabelOf } = params;
  const unitSortLabelOf = params.unitSortLabelOf ?? unitLabelOf;
  const building = invoices.filter(i => costSectionOf(i) === "building");
  const byUnit = new Map<string, TInvoice[]>();
  for (const invoice of invoices) {
    if (costSectionOf(invoice) !== "unit") continue;
    const unitId = String(invoice.unitId ?? "");
    byUnit.set(unitId, [...(byUnit.get(unitId) ?? []), invoice]);
  }
  const units = [...byUnit.entries()]
    .sort(([a], [b]) =>
      compareUnitLabels(unitSortLabelOf(a), unitSortLabelOf(b)),
    )
    .map(([unitId, unitInvoices]) => ({
      key: `unit:${unitId}`,
      section: "unit" as const,
      unitId,
      label: unitLabelOf(unitId),
      invoices: unitInvoices,
    }));
  return [
    ...(building.length > 0
      ? [
          {
            key: "building",
            section: "building" as const,
            label: params.buildingTitle ?? buildingLabel(),
            invoices: building,
          },
        ]
      : []),
    ...units,
  ];
};

// ── Tenant card ─────────────────────────────────────────────────────────────

// One slice of a lease's own provision as a statement and a summary card
// print it: its amount and its months.
export interface IProvisionSliceLine {
  slice: ProvisionSlice;
  amount: number;
  months?: number;
}

// The lease's own provision per slice, in the order common, private: the
// lines a building settlement prints instead of one "expected provisions"
// line. Together they make exactly the lease's provision (the provision
// without the ones added by hand); a lease without slice parts (an older
// snapshot, or one amount typed in for the whole lease), or whose parts do
// not add up to the cent, gets none and keeps the one line.
export const provisionSliceLines = (
  pc: Pick<
    PerContractResult,
    | "provision"
    | "extraProvisions"
    | "provisionBySlice"
    | "provisionMonthsBySlice"
  >,
): IProvisionSliceLine[] | undefined => {
  const bySlice = pc.provisionBySlice;
  if (!bySlice) return undefined;
  const lines = ALL_SLICES.flatMap(slice => {
    const amount = bySlice[slice];
    if (amount == null) return [];
    const months = pc.provisionMonthsBySlice?.[slice];
    if (Math.abs(amount) < 0.005 && !(months && months > 0)) return [];
    return [
      {
        slice,
        amount: roundCents(amount),
        ...(months != null ? { months } : {}),
      },
    ];
  });
  if (lines.length === 0) return undefined;
  const linesCents = lines.reduce((sum, line) => sum + toCents(line.amount), 0);
  return linesCents === toCents(leaseProvisionOf(pc)) ? lines : undefined;
};
